import { useCallback, useMemo, useRef } from 'react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import * as R from 'ramda';

import { Icon } from '@atom/mui';
import {
  getGoogleShapeCenter,
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
} from '@atom/utilities/mapUtilities';

import { useChatAssetDraftContext } from './ChatAssetDraftContext';

import classes from '../chat.module.css';

const MAP_CONTAINER_STYLE = { width: '100%', height: '15rem' };
const MAP_ZOOM = 16;
const DECIMAL_ACCURACY = 6;

const ChatAssetDraftMap = () => {
  const { draftAsset, disabled, handleDraftAssetChange } =
    useChatAssetDraftContext();

  const location = draftAsset?.location ?? null;
  const address = draftAsset?.address ?? '';
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    // @ts-ignore
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const center = useMemo(
    () => (location ? getGoogleShapeCenter(location) : null),
    [location],
  );

  const handleMapLoad = useCallback(() => {
    geocoderRef.current = new window.google.maps.Geocoder();
  }, []);

  const handleDragEnd = useCallback(
    (event: google.maps.MapMouseEvent) => {
      const lat = event?.latLng?.lat();
      const lng = event?.latLng?.lng();

      if (!lat || !lng) {
        return;
      }

      const newLocation = {
        type: 'Point',
        coordinates: [
          Number(lng.toFixed(DECIMAL_ACCURACY)),
          Number(lat.toFixed(DECIMAL_ACCURACY)),
        ],
      };

      if (geocoderRef.current) {
        geocoderRef.current.geocode({ location: { lat, lng } }, results => {
          const newAddress = R.pathOr('', [0, 'formatted_address'], results);
          handleDraftAssetChange({
            location: newLocation,
            address: newAddress,
          });
        });
      } else {
        handleDraftAssetChange({ location: newLocation, address: '' });
      }
    },
    [handleDraftAssetChange],
  );

  if (!location || !center) {
    return null;
  }

  if (!isLoaded) {
    return <div className={classes.assetDraftCardMapLoading} />;
  }

  return (
    <div className={classes.assetDraftCardMap}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={center}
        zoom={MAP_ZOOM}
        onLoad={handleMapLoad}
        options={{
          mapTypeId: 'roadmap',
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'cooperative',
        }}
      >
        <Marker
          position={center}
          draggable={!disabled}
          onDragEnd={disabled ? undefined : handleDragEnd}
        />
      </GoogleMap>
      {address && (
        <div className={classes.assetDraftCardAddress}>
          <Icon className={classes.assetDraftCardAddressIcon}>place</Icon>
          <span>{address}</span>
        </div>
      )}
    </div>
  );
};

export default ChatAssetDraftMap;
